import { createContext, useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { auth } from '@/firebase/config'
import { onAuthStateChanged } from 'firebase/auth'
import { isYouthEnvironment } from '@/config/youthEnvironment'
import {
  signIn,
  signUp,
  logOut,
  signInWithGoogle,
  signInWithFacebook,
  resetPassword,
  changePassword
} from '@/services/authServices'
import sessionTracker from '@/utils/sessionTracker'
import { enforceAuthSessionExpiry } from '@/utils/authSessionExpiry'
import { getUserByUid, updateUser } from '@/services/userService'
import { trackDailyPresence } from '@/utils/loginTracker'

const AuthContext = createContext(null)

export { AuthContext }

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === null) {
    console.warn(
      'useAuth called outside AuthProvider, returning default values'
    )
    return {
      currentUser: null,
      userRole: null,
      emailVerified: false,
      loading: true,
      setCurrentUser: () => {},
      setEmailVerified: () => {},
      signUp: async () => {},
      signIn: async () => {},
      signInWithGoogle: async () => {},
      signInWithFacebook: async () => {},
      resetPassword: async () => {},
      changePassword: async () => {},
      logOut: async () => {},
      fetchUserData: async () => {}
    }
  }
  return context
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [emailVerified, setEmailVerified] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hadUser, setHadUser] = useState(false)

  const fetchUserData = async (userId) => {
    try {
      const userData = await getUserByUid(userId)
      if (userData) {
        setUserRole(userData.role)

        // Track email verification status from Firestore
        // Default to false if not set (for backward compatibility)
        const isEmailVerified = userData.emailVerified === true
        setEmailVerified(isEmailVerified)

        // Check if auth email is different from user data email
        if (
          auth.currentUser?.email &&
          auth.currentUser.email !== userData.email
        ) {
          try {
            const updatedUserData = await updateUser(userId, {
              email: auth.currentUser.email,
              pendingEmail: null,
              updatedAt: new Date()
            })
            setCurrentUser({
              ...updatedUserData,
              uid: auth.currentUser.uid,
              email: auth.currentUser.email
            })
          } catch (_) {
            setCurrentUser({
              ...userData,
              uid: auth.currentUser.uid,
              email: auth.currentUser.email,
              pendingEmail: null
            })
          }
        } else {
          setCurrentUser({
            ...userData,
            uid: auth.currentUser.uid,
            email: auth.currentUser.email
          })
        }
      } else {
        setUserRole(null)
        setEmailVerified(false)
      }
    } catch (error) {
      setUserRole(null)
      setEmailVerified(false)
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const expired = await enforceAuthSessionExpiry(user)
        if (expired) {
          sessionTracker.endSession()
          setCurrentUser(null)
          setUserRole(null)
          setEmailVerified(false)
          setHadUser(false)
          setLoading(false)
          return
        }

        setCurrentUser(user)
        setHadUser(true)

        // Error reports carry the account id on adult pages only.
        Sentry.setUser(isYouthEnvironment() ? null : { id: user.uid })

        await fetchUserData(user.uid)
        sessionTracker.startSession(user.uid)
        void trackDailyPresence(user.uid)
      } else {
        sessionTracker.endSession()
        Sentry.setUser(null)

        setCurrentUser(null)
        setUserRole(null)
        setEmailVerified(false)
        setHadUser(false)
      }

      setLoading(false)
    })

    return () => {
      sessionTracker.endSession()
      unsubscribe()
    }
  }, [hadUser])

  // Add a useEffect to handle email updates
  useEffect(() => {
    if (currentUser?.email && currentUser.uid) {
      fetchUserData(currentUser.uid)
    }
  }, [currentUser?.email])

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        userRole,
        emailVerified,
        setEmailVerified,
        signUp,
        signIn,
        signInWithGoogle,
        signInWithFacebook,
        resetPassword,
        changePassword,
        logOut,
        loading,
        fetchUserData
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  )
}
